<template>
    <el-card shadow="never" class="component-container" :body-style="{padding: '10px', height: 'calc(100% - 20px)'}">
        <h4 style="margin: 0">
            {{ component.name }}
            <el-tooltip v-if="disabled" content="只有创建者才可以操作" placement="top">
                <el-icon class="color-info" style="float: right;">
                    <Lock/>
                </el-icon>
            </el-tooltip>
            <el-dropdown trigger="click" style="float: right;" v-else>
                <el-icon style="cursor: pointer">
                    <MoreFilled/>
                </el-icon>
                <template #dropdown>
                    <el-dropdown-menu>
                        <el-dropdown-item>
                            <div @click="$emit('edit')">
                                <el-icon>
                                    <Edit/>
                                </el-icon>
                                编辑
                            </div>
                        </el-dropdown-item>
                        <el-dropdown-item class="color-error">
                            <div @click="$emit('remove')">
                                <el-icon>
                                    <Delete/>
                                </el-icon>
                                删除
                            </div>
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </template>
            </el-dropdown>
            <el-icon class="handle" style="float: right;margin-right: 10px" v-if="!disabled">
                <Rank/>
            </el-icon>
            <el-icon style="float: right;margin-right: 10px;cursor: pointer" @click="$emit('view')">
                <View/>
            </el-icon>
            <el-icon v-if="!disabled" style="float: right;margin-right: 10px;cursor: pointer" @click="loadData(true)">
                <Refresh/>
            </el-icon>
        </h4>
        <div class="component-main" v-loading="loading">
            <template v-if="metric && valid">
                <p style="margin: 0;font-size: 12px" class="color-info">{{ subtitle }}</p>
                <component-chart :component="component" :metric="metric" :value="value" :value_date="value_date"
                                 :dod="dod" :wow="wow" :show-values="showValues" ref="chart"></component-chart>
            </template>
            <template v-else>
                <b class="component-error">{{ valid ? '指标已被删除' : '组件配置错误' }}</b>
            </template>
        </div>
    </el-card>
</template>

<script>
import {timestampToDate, generateDateRange, deepcopy} from "../../../libs/utils";
import axios from 'ts-axios-new';
import ComponentChart from "./ComponentChart";

export default {
    name: "ComponentGrid",
    components: {ComponentChart},
    props: {
        component: Object,
        disabled: Boolean,
        dateRange: Object,
        metricList: Array,
        showValues: Boolean,
        dashboardFilters: Array,
    },
    data() {
        return {
            loading: false, data: [], metric: null, subtitle: '',
            value: null, value_date: null, dod: null, wow: null,
            valid: true, requestSeq: 0,
        }
    },
    methods: {
        getDateRange() {
            const now = Date.now();
            if (this.dateRange) {
                const end = this.dateRange.end;
                let start = this.dateRange.start;
                if (new Date(end) - new Date(start) < 7 * 24 * 3600 * 1000) {
                    start = timestampToDate(new Date(end) - 7 * 24 * 3600 * 1000);
                }
                return {start, end};
            }
            let interval = 0;
            if (this.metric.category.indexOf('retain') > -1) {
                interval = this.metric.events[0].interval;
            }
            return {
                start: timestampToDate(now - (this.$root.t + this.component.latest_days + interval) * 24 * 3600 * 1000),
                end: timestampToDate(now - (this.$root.t + interval) * 24 * 3600 * 1000), chart: null,
            }
        },
        resize() {
            this.$refs.chart.resize();
        },
        filterValues(f) {
            if (!f) {
                return [];
            }
            const values = f.values !== undefined ? f.values : f.value;
            if (values === null || values === undefined || values === '') {
                return [];
            }
            return Array.isArray(values) ? values : [values];
        },
        emptyFilter(key) {
            if (key === 'living_days') {
                return {key, operator: '=', value: -1};
            }
            if (key === 'app_version') {
                return {key, operator: 'in', values: ['999999.999999.999999']};
            }
            return {key, operator: 'in', values: ['__NO_MATCH__']};
        },
        mergeTwoFilters(a, b) {
            if (!a) {
                return [deepcopy(b)];
            }
            if (!b) {
                return [deepcopy(a)];
            }
            const aValues = this.filterValues(a);
            const bValues = this.filterValues(b);
            if (a.operator === 'in' && b.operator === 'in') {
                const bSet = new Set(bValues);
                const values = aValues.filter(v => bSet.has(v));
                return [values.length ? {...deepcopy(a), values} : this.emptyFilter(a.key)];
            }
            if (a.operator === 'in' && b.operator === 'not in') {
                const bSet = new Set(bValues);
                const values = aValues.filter(v => !bSet.has(v));
                return [values.length ? {...deepcopy(a), values} : this.emptyFilter(a.key)];
            }
            if (a.operator === 'not in' && b.operator === 'in') {
                const aSet = new Set(aValues);
                const values = bValues.filter(v => !aSet.has(v));
                return [values.length ? {...deepcopy(b), values} : this.emptyFilter(a.key)];
            }
            if (a.operator === 'not in' && b.operator === 'not in') {
                return [{...deepcopy(a), values: Array.from(new Set([...aValues, ...bValues]))}];
            }
            return [deepcopy(a), deepcopy(b)];
        },
        mergeFilters(componentFilters, dashboardFilters) {
            const result = [];
            const dashboardQueue = (dashboardFilters || []).map(f => deepcopy(f));
            (componentFilters || []).forEach(f => {
                let merged = [deepcopy(f)];
                for (let i = dashboardQueue.length - 1; i >= 0; i--) {
                    if (dashboardQueue[i].key === f.key) {
                        const next = [];
                        merged.forEach(item => {
                            next.push(...this.mergeTwoFilters(item, dashboardQueue[i]));
                        });
                        merged = next;
                        dashboardQueue.splice(i, 1);
                    }
                }
                result.push(...merged);
            });
            result.push(...dashboardQueue);
            return result;
        },
        requestComponent() {
            const component = deepcopy(this.component);
            component.filters = this.mergeFilters(component.filters || [], this.dashboardFilters || []);
            return component;
        },
        init() {
            for (let i = 0; i < this.metricList.length; i++) {
                if (this.metricList[i].id === this.component.metric_id)
                    this.metric = this.metricList[i];
            }
            if (!this.metric)
                return
            this.loading = this.valid = true;
            this.component.groupings.forEach(g => {
                let valid = false;
                (this.component.filters || []).forEach(f => {
                    if (f.key === g) {
                        valid = true;
                    }
                });
                if (!valid) {
                    this.valid = valid;
                }
            });
            if (!this.valid) {
                this.loading = false;
                return
            }
            this.loadData()
        },
        loadData(no_cache = false) {
          this.loading = true
          this.value = this.value_date = this.dod = this.wow = null;
          const params = this.getDateRange();
          const requestSeq = ++this.requestSeq;
          const component = this.requestComponent();
          axios.post(`/api/v1/growth/data`, {
            no_cache: no_cache,
            component: component,
            product_id: this.$route.params.productId, ...params
          }).then(res => {
            if (requestSeq !== this.requestSeq) {
              return;
            }
            const rawData = res.data.data.data || [];
            const x = this.dateRange ? generateDateRange(this.dateRange.start, this.dateRange.end) : generateDateRange(params.start, params.end);
            const xSet = new Set(x);
            const rows = this.dateRange ? rawData.filter(d => xSet.has(d.date)) : rawData;
            if (rows.length) {
              if (this.component.category === 'number') {
                this.subtitle = this.value_date = rows[rows.length - 1].date.substring(5);
                this.value = rows[rows.length - 1].val;
                if (rows.length > 1)
                  this.dod = (this.value - rows[rows.length - 2].val) / rows[rows.length - 2].val * 100
                if (rows.length > 7)
                  this.wow = (this.value - rows[rows.length - 8].val) / rows[rows.length - 8].val * 100
              } else {
                this.subtitle = `${x[0].substring(5)} ~ ${x[x.length - 1].substring(5)}`
                if (!this.component.groupings.length) {
                  this.value_date = rows[rows.length - 1].date.substring(5);
                  this.value = rows[rows.length - 1].val;
                  if (rows.length > 1)
                    this.dod = (this.value - rows[rows.length - 2].val) / rows[rows.length - 2].val * 100
                  if (rows.length > 7)
                    this.wow = (this.value - rows[rows.length - 8].val) / rows[rows.length - 8].val * 100
                }
              }
              const series = [];
              const group_data = {};
              rows.forEach(d => {
                // if (x.indexOf(d.date.substring(5)) < 0) {
                //     x.push(d.date.substring(5));
                // }
                const groupings = [];
                this.component.groupings.forEach(g => {
                  groupings.push(d[g]);
                });
                const key = groupings.join('-');
                group_data[key] ? group_data[key].push([d.date, d.val]) : group_data[key] = [[d.date, d.val]];
              });
              for (let k in group_data) {
                series.push({
                  type: this.component.category,
                  name: k || this.component.name,
                  data: group_data[k]
                });
                if (series.length === this.component.limit)
                  break;
              }
              if (this.component.category !== 'number' && this.$refs.chart) {
                this.$refs.chart.draw(x, series);
              }
            } else if (this.component.category !== 'number' && this.$refs.chart) {
              this.$refs.chart.clear();
            }
            this.loading = false;
          })
        },
    },
    mounted() {
        this.init();
    }
}
</script>

<style scoped>
.component-container {
    width: 100%;
    height: 100%;
    overflow: unset;
}

.component-main {
    width: 100%;
    height: calc(100% - 22px);
    position: relative;
    padding: 5px 0;
}

.component-error {
    font-size: 1.5em;
    position: absolute;
    top: calc(50% - 17px);
    left: calc(50% - 77px);
    color: var(--el-text-color-placeholder)
}
</style>
